import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import api, { 
    getUserNotifications,
    getUserSupportTickets,
    updateUserDetails,
} from '../utils/api';
import { ThemeContext } from '../context/ThemeContext';
import styles from './UserDetailsPage.module.css';

import UserInfo from '../components/UserInfo';
import Portfolio from '../components/Portfolio';
import Transactions from '../components/Transactions';
import Funds from '../components/Funds';
import Orders from '../components/Orders';
import Notifications from '../components/Notifications';
import SupportTickets from '../components/SupportTickets';
import ImageModal from '../components/ImageModal';

const UserDetailsPage = () => {
    const { userId } = useParams();
    const navigate = useNavigate();

    const { theme } = useContext(ThemeContext);
    const [user, setUser] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [orders, setOrders] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [supportTickets, setSupportTickets] = useState([]);
    const [showImageModal, setShowImageModal] = useState(false);
    const [modalImageUrl, setModalImageUrl] = useState('');

    const [openSections, setOpenSections] = useState({}); // State for individual collapsible sections

    const toggleSection = (sectionName) => {
        setOpenSections(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }));
    };

    const fetchUserData = useCallback(async () => {
        try {
            const userRes = await api.get(`/api/admin/users/${userId}`);
            setUser(userRes.data);

            const transactionsRes = await api.get(`/api/admin/users/${userId}/transactions`);
            setTransactions(transactionsRes.data);

            const ordersRes = await api.get(`/api/admin/users/${userId}/orders`);
            setOrders(ordersRes.data);

        } catch (err) {
            console.error('[Admin Frontend] Failed to fetch user data', err);
        }
    }, [userId]);

    const fetchNotifications = useCallback(async () => {
        try {
            const notificationsRes = await getUserNotifications(userId);
            setNotifications(notificationsRes.data);
        } catch (err) {
            console.error('[Admin Frontend] Failed to fetch notifications', err);
        }
    }, [userId]);

    const fetchSupportTickets = useCallback(async () => {
        try {
            const supportRes = await getUserSupportTickets(userId);
            setSupportTickets(supportRes.data);
        } catch (err) {
            console.error('[Admin Frontend] Failed to fetch support tickets', err);
        }
    }, [userId]);


    useEffect(() => {
        fetchUserData();
        fetchNotifications();
        fetchSupportTickets();
    }, [userId, fetchUserData, fetchNotifications, fetchSupportTickets]);

    const handleImageClick = (imageUrl) => {
        setModalImageUrl(imageUrl);
        setShowImageModal(true);
    };

    const handleDeleteUser = async () => {
        if (window.confirm(`Are you sure you want to delete user ${user.name}?`)) {
            try {
                const renamedData = {
                    name: `del_${user.name}`,
                    email: `del_${user.email}`,
                    mobileNumber: `del_${user.mobileNumber}`,
                    password: `del_${user.password}`,
                };
                await updateUserDetails(user._id, renamedData);
                alert('User deleted successfully!');
                navigate('/');
            } catch (err) {
                console.error('[Admin Frontend] Failed to soft-delete user', err);
                alert('Failed to delete user');
            }
        }
    };

    if (!user) {
        return <div>Loading...</div>;
    }

    // Inlined CollapsibleSection component
    const renderCollapsibleSection = (title, content, sectionName) => (
        <div className={`${styles['collapsible-section']} ${styles[theme]}`}>
            <div className={styles['collapsible-section-header']} onClick={() => toggleSection(sectionName)}>
                <h3>{title}</h3>
                <span className={`${styles.arrow} ${openSections[sectionName] ? styles.open : ''}`}>&#9660;</span>
            </div>
            {openSections[sectionName] && (
                <div className={styles['collapsible-section-content']}>
                    {content}
                </div>
            )}
        </div>
    );

    return (
        <div className={`${styles['user-details-page']} ${styles[theme]}`}>
            <div className={styles.headerContainer}>
                <div className={styles.navControls}>
                    <h1 style={{ width: '100%', textAlign: 'center' }}>{user.name}</h1>
                </div>
            </div>

            {renderCollapsibleSection("User Information", <UserInfo user={user} setUser={setUser} onImageClick={handleImageClick} onSaveSuccess={fetchUserData} />, "userInfo")}
            {renderCollapsibleSection("Portfolio", <Portfolio user={user} userId={userId} onSaveSuccess={fetchUserData} />, "portfolio")}
            {renderCollapsibleSection("Transactions", <Transactions 
                userId={userId} 
                transactions={transactions} 
                setTransactions={setTransactions} 
                onImageClick={handleImageClick}
                onSaveSuccess={fetchUserData} // Transactions can affect fund/portfolio data
            />, "transactions")}
            {renderCollapsibleSection("Funds", <Funds userId={userId} user={user} setUser={setUser} onSaveSuccess={fetchUserData} />, "funds")}
            {renderCollapsibleSection("Orders", <Orders orders={orders} setOrders={setOrders} onSaveSuccess={fetchUserData} />, "orders")}
            {renderCollapsibleSection("Notifications", <Notifications 
                userId={userId} 
                notifications={notifications} 
                setNotifications={setNotifications} 
                onSaveSuccess={fetchNotifications} 
            />, "notifications")}
            {renderCollapsibleSection("Help & Support", <SupportTickets 
                supportTickets={supportTickets} 
                setSupportTickets={setSupportTickets} 
                onImageClick={handleImageClick}
                onSaveSuccess={fetchSupportTickets} 
            />, "helpAndSupport")}

            <div style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}>
                <button className={styles['delete-button']} onClick={handleDeleteUser} style={{ width: '80%', maxWidth: '400px', padding: '12px', fontSize: '1rem', fontWeight: 'bold' }}>
                    Delete User
                </button>
            </div>

            <ImageModal 
                isOpen={showImageModal} 
                imageUrl={modalImageUrl} 
                onClose={() => setShowImageModal(false)} 
            />
        </div>
    );
};

export default UserDetailsPage;
